
import { msalInstance, loginRequest } from '@/config/msal'
import { InteractionType, InteractionRequiredAuthError } from '@azure/msal-browser'
import { callMsGraph } from '@/utils/MsGraphApiCall'
import store from '@/store/index'
import { MessagesService, ParametersService } from '@/services'
import logger from '@/utils/logger'
// Hash problem https://github.com/AzureAD/microsoft-authentication-library-for-js/pull/4415

const POST_LOGIN_DIALOG_KEY = 'postLoginDialogShown_v2_20260316'

/**
 * Affiche un dialog d'information une seule fois après la connexion de l'utilisateur.
 *
 * Utilise le sessionStorage pour s'assurer que le dialog n'est affiché qu'une seule fois
 * par session de navigateur. La clé POST_LOGIN_DIALOG_KEY est versionnée (ex: _v2_20260316)
 * pour forcer le réaffichage quand le message change.
 *
 * Fonctionnement :
 * 1. Vérifie si le dialog a déjà été affiché dans cette session (via sessionStorage)
 * 2. Si oui, on sort immédiatement (return) — le dialog ne s'affiche pas deux fois
 * 3. Si non, on marque la clé dans sessionStorage pour ne plus le réafficher
 * 4. On commit le message HTML dans le store Vuex (module dialog) pour l'afficher à l'écran
 *
 * Appelée dans le router guard (registerGuard) après chaque authentification réussie.
 * La fonction resetPostLoginDialog() efface la clé lors du logout pour permettre
 * le réaffichage à la prochaine connexion.
 */
async function showPostLoginDialogOnce () {
  // Si le paramètre getNotificationMessageConnection n'est pas activé ('1'), on n'affiche pas le dialog
  const notificationEnabled = store.getters['parameters/getNotificationMessageConnection']
  if (notificationEnabled !== '1') return

  // Si le dialog a déjà été affiché dans cette session, on ne le réaffiche pas
  if (sessionStorage.getItem(POST_LOGIN_DIALOG_KEY)) return

  // On marque le dialog comme affiché pour cette session
  sessionStorage.setItem(POST_LOGIN_DIALOG_KEY, '1')

  // Récupère le message no 41 depuis l'API
  const messageData = await MessagesService.search(41)
  if (!messageData) return

  // Utilise le message selon la langue de l'application (fr ou en)
  const langue = localStorage.getItem('APP_LANG_KEY') || 'fr'
  const message = messageData[langue] || messageData.fr || ''
  if (!message) return

  // Affiche le dialog via le store Vuex (module dialog)
  store.commit('dialog/showDialog', {
    message, // Contenu HTML du message récupéré de la DB
    print: false, // Pas d'option d'impression
    html: true, // Le message est en HTML (pas du texte brut)
    iconState: true, // Affiche une icône
    iconColor: 'success', // Couleur verte
    iconName: 'mdi-check-circle', // Icône de succès
  })
}

function resetPostLoginDialog () {
  sessionStorage.removeItem(POST_LOGIN_DIALOG_KEY)
}

export function registerGuard (router) {
  router.beforeEach(async (to, from, next) => {
    // Si la page n'est pas public
    if (to.meta.requiresAuth) {
      const request = {
        ...loginRequest,
        redirectStartPage: to.fullPath,
      }
      const shouldProceed = await isAuthenticated(msalInstance, InteractionType.Redirect, request)
      if (shouldProceed) {
        // Load parameters
        await loadParameters(next)
        // Fenetre affichee une seule fois apres connexion
        await showPostLoginDialogOnce()
        // CheckRole
        checkRole(to.meta.roles, msalInstance, to, next)
      } else {
        // On enlève les infos de l'utilisateur
        store.commit('userInfo/resetUser')
        // On vide les paramètres pour forcer le rechargement à la prochaine connexion
        store.commit('parameters/setParameters', [])
        resetPostLoginDialog()
        next('/login')
      }
    } else {
      // La page est public
      // Si la page est 'Login', on vérifie si l'utilisateur est authentifié
      if (to.name === 'Login') {
        const isAuthenticated = await checkIfAuthenticated(msalInstance)
        // Si l'utilisateur est authentifié, on retourne à la page d'acceuil
        if (isAuthenticated) {
          // Load parameters
          await loadParameters(next)
          // Fenetre affichee une seule fois apres connexion
          await showPostLoginDialogOnce()
          // Go to Portal
          next('/my_portal')
        } else {
          // On enlève les infos de l'utilisateur
          store.commit('userInfo/resetUser')
          // On vide les paramètres pour forcer le rechargement à la prochaine connexion
          store.commit('parameters/setParameters', [])
          resetPostLoginDialog()
        }
      }
      next()
    }
  })
}

export async function isAuthenticated (instance, interactionType, loginRequest) {
  // MSAL v3: handleRedirectPromise is now called once in main.js at app startup
  // Here we just check if the user is already authenticated
  try {
    const accounts = instance.getAllAccounts()
    if (accounts.length > 0) {
      // Mise à jour des infos de l'utilisateur
      await updateUserInfo(accounts[0])
      // Get token silently
      try {
        await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        return true
      } catch (err) {
        // Si on ne réussi pas à récupérer le token, il faut s'authentifier
        if (err instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return instance.acquireTokenRedirect(loginRequest).catch((err) => {
            logger.error(err)
            return false
          })
        }
      }
    }

    // User is not signed in and attempting to access protected route. Sign them in.
    if (interactionType === InteractionType.Popup) {
      return instance.loginPopup(loginRequest).then(() => {
        return true
      }).catch((err) => {
        logger.error(err)
        return false
      })
    } else if (interactionType === InteractionType.Redirect) {
      return instance.loginRedirect(loginRequest).then(() => {
        return true
      }).catch((err) => {
        logger.error(err)
        return false
      })
    }

    return false
  } catch (error) {
    logger.error('isAuthenticated error:', error)
    return false
  }
}

async function checkIfAuthenticated (instance) {
  // MSAL v3: handleRedirectPromise is now called once in main.js at app startup
  // Here we just check if accounts exist
  try {
    const accounts = instance.getAllAccounts()
    if (accounts.length > 0) {
      // Mise à jour des infos de l'utilisateur
      await updateUserInfo(accounts[0])
      return true
    }
    return false
  } catch (error) {
    logger.error('checkIfAuthenticated error:', error)
    return false
  }
}

function checkRole (roles, instance, to, next) {
  // Si le rôle est user. L'utilisateur peut y aller
  if (roles.some(item => item === 'user')) return next()
  // Récupération des rôles de l'utilisateur
  const accounts = instance.getAllAccounts()
  const idTokenClaims = accounts[0]?.idTokenClaims || {}
  const userRoles = Array.isArray(idTokenClaims.roles) ? idTokenClaims.roles : []
  if (userRoles.length === 0) {
    // Aucun rôle trouvé, accès refusé
    if (to.path !== '/unauthorized') {
      next('/unauthorized')
    }
    return
  }
  const found = userRoles.some(r => roles.indexOf(r) >= 0)
  // Role not found
  if (!found) {
    // Go to unauthorized page. If not loop redirecting
    if (to.path !== '/unauthorized') {
      next('/unauthorized')
    }
  } else {
    next()
  }
}

async function updateUserInfo (account) {
  // Si le username est différent, on le mets à jour synchrone
  if (store.state.userInfo.info.usernameVerification !== account.username) {
    const response = await callMsGraph()
    const userInfo = {
      name: response.surname,
      firstName: response.givenName,
      mail: response.mail,
      displayName: account.name,
      username: response.userPrincipalName,
      usernameVerification: account.username,
      roles: account.idTokenClaims.roles ? account.idTokenClaims.roles : [],
    }
    store.commit('userInfo/updateUser', userInfo)
  } else {
    // Mets à jour asynchrone
    callMsGraph().then(response => {
      const userInfo = {
        name: response.surname,
        firstName: response.givenName,
        mail: response.mail,
        displayName: account.name,
        username: response.userPrincipalName,
        usernameVerification: account.username,
        roles: account.idTokenClaims.roles ? account.idTokenClaims.roles : [],
      }
      store.commit('userInfo/updateUser', userInfo)
    })
  }
}

async function loadParameters (next) {
  try {
    // On regarde si les paramètres sont déjà là. Si il n'y sont pas ont l'ai récupère
    if (!store.state.parameters.parameters.length) {
      // Liste des paramètres que l'on mets dans le vuex pour récupéré partout dans le code
      const parameters = await ParametersService.listAll()
      store.commit('parameters/setParameters', parameters)
    }
  } catch (error) {
    logger.info(error)
    next('/error500')
  }
}
